import { LockSimpleIcon, XIcon } from '@phosphor-icons/react'
import type React from 'react'

import LoadingDots from '../_shared/LoadingDots'
import PriceLockRow from '../_shared/PriceLockRow'
import PurchaseStatusBadge from '../_shared/PurchaseStatusBadge'
import SkeletonTextLines from '../_shared/SkeletonTextLines'
import TextShell from '../_shared/TextShell'

import type {
  TextCardDraftProps,
  TextCardOtherProps,
  TextCardOwnProps,
  TextCardPreviewProps,
  TextCardProps,
} from './types'

export type { TextCardProps } from './types'

const DraftTextCard: React.FC<TextCardDraftProps> = ({
  renderedText,
  amountText,
  placeholderAmountText,
  onDismiss,
}) => (
  <TextShell isMine className="pr-8">
    {renderedText || <SkeletonTextLines variant="dark" />}
    <PriceLockRow>
      <span className={amountText ? 'text-white/55' : 'text-white/30'}>
        {amountText || placeholderAmountText}
      </span>
    </PriceLockRow>
    {onDismiss && (
      <button
        type="button"
        onClick={onDismiss}
        className="absolute right-3 top-3 flex size-6 shrink-0 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/15"
        aria-label="Dismiss attachment"
      >
        <XIcon className="size-3" weight="bold" />
      </button>
    )}
  </TextShell>
)

const PreviewTextCard: React.FC<TextCardPreviewProps> = ({
  amountText,
  placeholderAmountText,
}) => (
  <TextShell isMine={false}>
    <SkeletonTextLines variant="light" />
    <PriceLockRow>
      <span className="opacity-55">{amountText || placeholderAmountText}</span>
    </PriceLockRow>
  </TextShell>
)

const OwnTextCard: React.FC<TextCardOwnProps> = ({
  renderedText,
  amountText,
  paymentStatus = 'pending',
}) => {
  const isPaid = paymentStatus === 'paid'

  return (
    <TextShell isMine>
      {isPaid && renderedText ? (
        renderedText
      ) : (
        <SkeletonTextLines variant="dark" />
      )}
      <PriceLockRow>
        {amountText && <span className="text-white/55">{amountText}</span>}
        <PurchaseStatusBadge isMine paymentStatus={paymentStatus} />
      </PriceLockRow>
    </TextShell>
  )
}

const OtherTextCard: React.FC<TextCardOtherProps> = ({
  renderedText,
  amountText,
  paymentStatus,
  onUnlockClick,
  isUnlocking = false,
}) => {
  const isPaid = paymentStatus === 'paid'

  if (isPaid && renderedText) {
    return (
      <TextShell isMine={false}>
        {renderedText}
        <PriceLockRow>
          {amountText != null && (
            <span className="opacity-55">{amountText}</span>
          )}
          <PurchaseStatusBadge isMine={false} paymentStatus="paid" />
        </PriceLockRow>
      </TextShell>
    )
  }

  const ariaLabel = isUnlocking ? 'Unlocking message.' : 'Unlock this message.'

  return (
    <TextShell isMine={false}>
      <div className="flex items-center gap-3">
        <div className="flex flex-1 flex-col gap-2">
          <SkeletonTextLines variant="light" />
          <span className="sr-only" aria-live="polite">
            {ariaLabel}
          </span>
          <PriceLockRow>
            {amountText != null && (
              <span className="opacity-55">{amountText}</span>
            )}
          </PriceLockRow>
        </div>
        <button
          type="button"
          onClick={onUnlockClick}
          disabled={isUnlocking}
          aria-label={isUnlocking ? 'Unlocking message' : 'Unlock message'}
          aria-busy={isUnlocking}
          title={isUnlocking ? 'Unlocking message' : 'Unlock message'}
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#121110] text-white hover:bg-[#2a2928]"
        >
          {isUnlocking ? (
            <LoadingDots />
          ) : (
            <LockSimpleIcon className="size-4" weight="fill" aria-hidden />
          )}
        </button>
      </div>
    </TextShell>
  )
}

const TextCard: React.FC<TextCardProps> = (props) => {
  if (props.isDraft) {
    return <DraftTextCard {...props} />
  } else if (props.isPreview) {
    return <PreviewTextCard {...props} />
  } else if (props.isMine) {
    return <OwnTextCard {...props} />
  } else {
    return <OtherTextCard {...props} />
  }
}

export default TextCard
